<template>
  <PageLayout>
    <template #header>
      <ClientSelection  v-if="isMSMUser"
                        :disabled="appLoading"
                        @clientselectionchanged="onClientSelectionChanged"/>
      <PageHeader :user-name="userDisplayName"
                  :welcome-message="$t('Webportal.Welcome.Dashboard')"
                  @refresh="handleRefresh" />
    </template>

    <template #main>
      <LegendList :showTick="false" />
      <div v-for="(board, idx) in boardSetup?.boards || []" :key="idx">
        <div class="py-3">
          <KpiBoard v-if="board.type === 'kpi'"
                    :boardIndex="idx"
                    :board-setup="boardSetup"
                    :board-data="getBoardData(idx)"
                    :wave-data="waveData"
                    @add-kpis="handleAddKpisFromBoard" />

          <WaveBoard  v-if="board.type === 'wave'"
                      :boardIndex="idx"
                      :board-setup="boardSetup"
                      :board-data="getBoardData(idx)"
                      :wave-data="waveData"
                      @detail-click="handleWaveDetailClick"
                      @wave-click="handleWaveClick" />
        </div>
      </div>

      <div v-if="boardSetup && waveData" >
        <div class="py-3">
        <WaveBoard  :boardIndex="3"
                    :board-setup="boardSetup"
                    :board-data="waveData"
                    :wave-data="waveData" />
        </div>
      </div>

      <ModalCreateCustomView ref="modalCreateCustomView" @next="handleNextFromCreateView" />
      <ModalConfirmDelete ref="modalConfirmDelete" />
      <ModalCustomizeKpis v-if="showModalCustomizeKpis"
                          v-model="showModalCustomizeKpis"
                          :kpis="customizeKpisInitialKpis"
                          :board-setup="boardSetup"
                          :index="customizeKpisIndex"
                          :creation-mode="pendingBoardCreation !== null"
                          :pending-board-settings="pendingBoardCreation" />
    </template>
  </PageLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import PageLayout from '@/componentsv3/PageLayout.vue';
import PageSidebar from '@/componentsv3/PageSidebar.vue';
import PageHeader from '@/componentsv3/PageHeader.vue';
import UiDataService from '@/services/UiDataService';
import ClientSelection from '@/components/ClientSelection.vue';
import CancelToken from '@/mixins/CancelToken';
import SideNotes from '@/componentsv3/SideNotes.vue';
import LegendList from '@/componentsv3/LegendList.vue';
import KpiBoard from '@/componentsv3/KpiBoard/KpiBoard.vue';
import KpiSearchBar from '@/componentsv3/KpiSearchBar.vue';
import WaveBoard from '@/componentsv3/WaveBoard/WaveBoard.vue';
import { useBoardsHelpers } from '@/composables/boards-helpers';
import { assignKpiPerformanceIndexOverview } from '@/utils/kpiIndexHelpers';
import { CalendarArrowUp } from 'lucide-vue-next';
import ModalCreateCustomView from '@/componentsv3/general/ModalCreateCustomView.vue';
import ModalConfirmDelete from '@/componentsv3/general/ModalConfirmDelete.vue';
import ModalCustomizeKpis from '@/componentsv3/KpiBoard/ModalCustomizeKpis.vue';
import DownloadFileMixin from '@/mixins/DownloadFileMixin'
import fakeWaveData from '@/assets/WaveData.json';

export default {
  name: 'Overview',

  mixins: [CancelToken, DownloadFileMixin],

  provide() {
    return {
      delegateCreateView: this.handleCreateView,
      delegateChangeView: this.handleChangeView,
      delegateCustomizedBoard: this.inboundCustomizedBoard,
      delegateEditBoardDescription: this.inboundEditBoardDescription,
      delegateDeleteView: this.handleDeleteView,
      delegateCreateBoardWithKpis: this.inboundCreateBoardWithKpis,
      delegateExportWaves: this.handleExportWaves
    }
  },

  components: {
    PageLayout,
    PageSidebar,
    PageHeader,
    KpiBoard,
    KpiSearchBar,
    WaveBoard,
    ClientSelection,LegendList,
    SideNotes,
    ModalCreateCustomView,
    ModalConfirmDelete,
    ModalCustomizeKpis
  },

  setup() {
    const BOARDS = useBoardsHelpers();
    return { BOARDS };
  },

  mounted() {
    this.loadOverviewData();
  },

  beforeUnmount() {
    this.cancelCancelToken(this.dashboardDataCancelToken);
    this.setAppLoading(false);
  },

  watch: {
    isClientReady(newVal) {
      this.loadOverviewData();
    },
    filters(newVal) {
      this.loadOverviewData();
    },
    surveyIds(newVal) {
      this.loadOverviewData();
    },
  },

  data() {
    return {
      activeViewId: null, // null = Backend lädt die zuletzt angesehene View des Users
      boardData: [],
      boardSetup: undefined,
      CalendarArrowUp, // notwendig für SideNotes-Komponente
      customizeKpisIndex: null,
      customizeKpisInitialKpis: [],
      dashboardDataCancelToken: null,
      overviewLoaded: false,
      pendingBoardCreation: null,
      showModalCustomizeKpis: false,
      sideBarNotifications: {
        lastExports: [],
        upcomingAppointments: []
      },
      structureItems: [],
      waves: [],
      waveData: {},
    }
  },

  computed: {
    ...mapGetters('auth', [ 'clientId', 'isMSMUser', 'userDisplayName' ]),
    ...mapGetters('clientData', [ 'appLoading', 'brandIds', 'filters', 'isClientReady', 'structure', 'surveyIds' ]),
    ...mapGetters('localization', [ 'currentLocale' ]),
  },

  methods: {
    ...mapActions('app', [ 'toggleShowOffcanvas' ]),
    ...mapActions('clientData', [ 'setAppLoading' ]),

    getEmptyBoard() {
      return {
        id: null,
        label: "New Board",
        description: "default empty board",
        position: 1,
        type: "kpi",
        isStandard: true,
        isActive: true,
        controls: []
      };
    },

    getFilterSettings() {
      const filter = {
        api: APP_TARGET === 'bmw' ? 'bmw2021experimental' : undefined,
        brand: APP_TARGET === 'bmw' ? this.brandIds[0] : undefined, // '1105^BMW', '1105^BMW|1105^MINI',
        clientId: this.clientId,
        projects: null,
        waves: '1405,1404,1403,1402,1401,1400,1399,1398,1397,1396,1395,1394,1393,1392,1391,1390,1389,1388,1387,1386,1385,1384,1383,1382,1381,1380,1379,1378,1377,1376,1375,1374,1373,1372,1371,1370,1369,1368,1367,1366,1365,1364,1363,1362,1361,1360,1359,1358,1357,1356,1355,1354,1353,1352',
        //this.BOARDS.getWaves(this.filters.Waves, this.boards).join(','), // '1044,1174,1351,1352',
        regions: null, // '1106^C1',
        subregions: null,
        nscs: null,
        outlets: null,
        nscsub: null,
        areas: null,
        dealergroups: null,
        contactinitiation: null,
        surveys: '-49,-48,-47', //'-52,-51,-50,-49,-48,-47,-46,-45,-44,-43,-42,-41',
        //this.BOARDS.getSurveys(this.filters.Waves, this.boards).join(','),
        // "-46,-53,-54,-41,-45,-47,-44,-42,-43", // surveys: [...this.surveyIds],
        page: 'overview',
       // relevantKpis: this.BOARDS.getRelevantKpis(this.boards).join(',')
      };
      return filter;
    },

    async loadOverviewData(refresh) {
      if (!refresh) {
        if (this.overviewLoaded || !this.isClientReady ||  this.appLoading || !this.filters || !this.clientId || !this.currentLocale) {
          return;
        }
      }

      this.overviewLoaded = true;
      this.setAppLoading(true);
      this.dashboardDataCancelToken = this.initCancelToken(this.dashboardDataCancelToken);

      const [lastWaves, boardSetup] = await Promise.allSettled([
        //sideBar ... NotificationService.getNotificationFeed(), // this.clientId, this.currentLocale
        UiDataService.readWaveStatus(this.clientId, this.currentLocale),
        UiDataService.readView(this.clientId, this.activeViewId, this.currentLocale)
      ]).catch(error => {
        log.error('Error loading overview data:', error);
        return [null, null];
      });

      assignKpiPerformanceIndexOverview(boardSetup.value.boards[0]?.controls || []);
      // this.sideBarNotifications = sideBar;
      this.waveData = fakeWaveData;

      const waveIds = this.waveData?.Waves?.flatMap(w => w.WaveIds?.split(',').filter(id => id.trim()) || []) || [];
      this.boardSetup = boardSetup.value;
      this.activeViewId = this.boardSetup?.id || null;
      if (this.boardSetup.boards.length === 0) {
        this.boardSetup.boards = [this.getEmptyBoard()];
      }
      this.boardData = Array.from({ length: this.boardSetup.boards.length }, () => ([]));

      const dfltFilter = JSON.parse("{\"Brand\":[\"n1105^BMW\",\"n1105^GKL\",\"n1105^xEV (BMW i)\",\"n1105^xEV (BMW i)/GKL\",\"n1105^xEV (PHEV)\",\"n1105^xEV (PHEV)/GKL\"],\"Surveys\":[\"-46\",\"-53\",\"-54\",\"-48,-41\",\"-52,-45\",\"-47\",\"-51,-44\",\"-49,-42\",\"-50,-43\"],\"Waves\":[],\"Regions\":[],\"Subregions\":[],\"NSCs\":[],\"SubNSCs\":[],\"Areas\":[],\"Dealergroups\":[],\"ClientLocations\":[]}");
      dfltFilter.Waves = waveIds;
      const kpiCalls = (this.boardSetup.boards || [])
        .filter(b => b.type === 'kpi')
        .flatMap((b, index) => (b.controls || []).filter(c => c.type !== 'linebreak').map(c => {
          const filter = c.filter === '{}' ? { ...dfltFilter } : JSON.parse(c.filter);
          filter.Waves = waveIds;
          filter.Surveys = filter.Surveys.flatMap(i => Array.isArray(i) ? i : [i]).map(s => String(s));
          return {
            id: c.id,
            boardIndex: index,
            kpiId: c.kpiId,
            kpiGroupId: c.kpiGroupId,
            kpiCode: c.kpiCode,
            kpiGroupCode: c.kpiGroupCode,
            label: c.label,
            filter: c.filter,
            parsedFilter: filter
          };
        }));
      const promises = kpiCalls.map(item => Promise.resolve(this.buildFakeTileData(item)));
      const callResults = await Promise.allSettled(promises);
      for (let i = 0; i < kpiCalls.length; i++) {
        const result = callResults[i];
        const item = kpiCalls[i];
        this.boardData[item.boardIndex].push(result.status === 'fulfilled' ? result?.value : null);
      }

      // this.boardData =
      // const [dashboardResponse] = await Promise.all([
      //   DashboardService.getDashboard(filter, this.dashboardDataCancelToken, this.currentLocale)
      // ]).catch(error => {
      //   log.error('Error loading overview data:', error);
      //   return [null, null];
      // });
      // this.boardData = dashboardResponse?.data;
      this.setAppLoading(false);
    },

    getBoardData(index) {
      return this.boardData[index] || [];
    },

    buildFakeTileData(item) {
      const waveNames = (this.waveData?.Waves || []).map(w => w.Name).reverse();
      const label = item.label || '';
      const [groupName, shortNameRaw] = label.includes(' - ')
        ? [label.split(' - ')[0], label.split(' - ').slice(1).join(' - ')]
        : ['Lead', label];
      const shortName = shortNameRaw || 'KPI';

      const fakeResultsByShortName = {
        'Communication Quality':  [86, 89, 92],
        'Framework / Process':    [90, 88, 87],
        'Overall Experience':     [84, 87, 90],
        'Response Time':          [79, 83, 86]
      };
      const groupResults = [85, 87, 89];
      const hashSeed = [...(item.kpiCode || shortName)].reduce((a, c) => a + c.charCodeAt(0), 0);
      const fallback = [0, 2, 4].map(o => 78 + ((hashSeed + o) % 15));
      const results = fakeResultsByShortName[shortName] || fallback;

      const sampleSize = 800 + (hashSeed % 250);
      const buildWaves = (values) => waveNames.map((name, i) => {
        const result = values[i] ?? values[values.length - 1];
        const instances = sampleSize + i * 40;
        const pointsMax = instances * 1.1;
        const pointsReached = Math.round(pointsMax * (result / 100) * 100) / 100;
        return {
          Name: name,
          Year: 2026,
          Result: result,
          SurveyInstanceCount: instances,
          PointsReached: pointsReached,
          PointsMax: Math.round(pointsMax * 100) / 100
        };
      });

      return {
        KPI: [{
          KpiCode: item.kpiCode,
          KpiName: label || shortName,
          KpiNameShort: shortName,
          KpiGroupCode: item.kpiGroupCode,
          SurveyWave: buildWaves(results)
        }],
        KPIGroup: [{
          KpiCode: item.kpiGroupCode,
          KpiName: groupName,
          SurveyWave: buildWaves(groupResults)
        }]
      };
    },

    handleRefresh() {
      log.info('Refreshing overview data...');
      // Hier deine Refresh-Logik
    },

    completeOverview() {
      log.log('Overview completed!');
      // Hier deine Completion-Logik
    },

    handleWaveDetailClick() {
      log.log('Navigate to wave details');
    },

    handleWaveClick(wave) {
      log.log('Wave clicked:', wave);
    },

    async inboundCustomizedBoard(data) {
      const board = this.boardSetup.boards[data.index];
      if (!board) {
        throw new Error("No Board found at index " + data.index);
      } else {
        board.controls = data.kpis;
        assignKpiPerformanceIndexOverview(board.controls);
      }

      // Persistiere die Änderungen (alle Properties außer 'views')
      const { views, ...viewUpdate } = this.boardSetup;
      await UiDataService.updateView(
        this.clientId,
        this.currentLocale,
        viewUpdate
      );

      this.loadOverviewData(true);
    },

    async inboundEditBoardDescription(data) {
      try {
        if (this.boardSetup.id !== data.index) {
          throw new Error("View ID mismatch: expected " + this.boardSetup.id + " but got " + data.index);
        }

        const response = await this.$refs.modalCreateCustomView.open(this.boardSetup, true);

        this.handleChangeView(response);
      } catch (error) {
        log.error('View creation cancelled', error);
      }
    },

    async onClientSelectionChanged(clientSettings) {
      if (this.appLoading || !this.isClientReady) return;
      this.loadOverviewData();
    },

    async handleCreateView(boardSetup) {
      try {
        const boardSettings = await this.$refs.modalCreateCustomView.open(boardSetup);

        // Persistiere die Änderungen
        let response = await UiDataService.createView(
          this.clientId,
          'overview',
          this.currentLocale,
          boardSettings
        );

        this.handleChangeView(response);
      } catch (error) {
        log.error('View creation cancelled', error);
      }
    },

    handleNextFromCreateView({ boardSettings, boardSetup }) {
      // Speichere die Board-Settings für spätere Verwendung
      this.pendingBoardCreation = boardSettings;

      // Bestimme initiale KPIs basierend auf copyCurrentLayout
      if (boardSettings.copyCurrentLayout && boardSetup?.boards?.[0]?.controls) {
        // Layout übernehmen: KPIs vom aktuellen/source Board kopieren
        this.customizeKpisInitialKpis = JSON.parse(JSON.stringify(boardSetup.boards[0].controls));
      } else {
        // Leeres Board
        this.customizeKpisInitialKpis = [];
      }

      // Öffne ModalCustomizeKpis im Creation-Modus
      this.customizeKpisIndex = 0; // Neues Board ist immer an Index 0
      this.showModalCustomizeKpis = true;
    },

    handleAddKpisFromBoard(boardIndex) {
      // Öffne ModalCustomizeKpis im Edit-Modus für existierendes Board
      this.pendingBoardCreation = null; // Kein Creation-Modus
      this.customizeKpisIndex = boardIndex;

      // Lade bestehende KPIs des Boards
      this.customizeKpisInitialKpis = this.boardSetup?.boards?.[boardIndex]?.controls
        ? JSON.parse(JSON.stringify(this.boardSetup.boards[boardIndex].controls))
        : [];

      this.showModalCustomizeKpis = true;
    },

    async inboundCreateBoardWithKpis({ boardSettings, kpis }) {
      try {
        // Board erstellen (POST)
        const createdView = await UiDataService.createView(
          this.clientId,
          'overview',
          this.currentLocale,
          boardSettings
        );

        // Sofort KPIs speichern (PUT) - nur wenn KPIs vorhanden
        if (kpis && kpis.length > 0 && createdView.boards && createdView.boards.length > 0) {
          // viewUpdate mit allen Properties aus boardSettings ergänzen
          const { views, ...viewUpdate } = createdView;
          viewUpdate.boards[0].controls = kpis;
          viewUpdate.boards[0].label = boardSettings.name; // label = name
          viewUpdate.boards[0].description = boardSettings.description;
          viewUpdate.boards[0].type = 'kpi'; // label = name
          viewUpdate.boards[0].position = 1;
          viewUpdate.boards[0].isActive = true;
          viewUpdate.boards[0].isStandard = true;
          viewUpdate.name = boardSettings.page;
          viewUpdate.label = boardSettings.name; // label = name
          viewUpdate.description = boardSettings.description;
          viewUpdate.isActive = boardSettings.isActive;
          viewUpdate.isGeneric = boardSettings.isGeneric;

          await UiDataService.updateView(
            this.clientId,
            this.currentLocale,
            viewUpdate
          );
        }

        // Schritt 3: UI aktualisieren
        this.handleChangeView(createdView);

        // Cleanup
        this.pendingBoardCreation = null;
        this.showModalCustomizeKpis = false;
      } catch (error) {
        log.error('Error creating board with KPIs:', error);
        this.pendingBoardCreation = null;
        this.showModalCustomizeKpis = false;
      }
    },

    async handleDeleteView(view) {
      try {
        await this.$refs.modalConfirmDelete.open({
          title: 'View löschen',
          message: 'Möchten Sie diese Ansicht wirklich löschen?',
          itemName: view.label,
          warning: 'Alle zugehörigen Boards und KPIs werden ebenfalls gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.'
        });

        await UiDataService.deleteView(view.id);
        this.activeViewId = this.boardSetup?.views[0]?.id || null;
        this.loadOverviewData(true);
      } catch (error) {
        log.error('Error deleting view:', error);
      }
    },

    handleChangeView(newView) {
      this.activeViewId = newView.id;
      this.loadOverviewData(true);
    },

    async handleExportWaves() {
      const blob = await UiDataService.exportWaveStatus(this.currentLocale);
      this.downloadFile(blob);
    },
  }
}
</script>

<style lang="scss">
.container-fluid {
  padding-left: unset !important;
  padding-right: unset !important;
}
</style>

<style lang="scss" scoped>
.overview-main-content {
  padding: 0 32px 32px;

  @media (max-width: 991px) {
    padding: 0 16px 16px;
  }

  h1 {
    font-size: 24px;
    font-weight: 600;
    color: var(--color-foreground);
    margin-bottom: 16px;
  }

  p {
    color: var(--color-muted-foreground);
    line-height: 1.6;
    margin-bottom: 12px;
  }
}

.kpi-autocomplete-section {
  margin: 24px 0;
  position: relative;
}

.autocomplete-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  max-height: 300px;
  overflow-y: auto;
  z-index: 10;
}

.autocomplete-item {
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid var(--color-border);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: var(--color-accent);
  }

  strong {
    color: var(--color-foreground);
    font-weight: 600;
  }

  span {
    color: var(--color-muted-foreground);
    font-size: 14px;
  }
}

.custom-title {
  font-size: 28px;
  font-weight: 400;
  color: var(--color-foreground);
}

.overview-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-item {
  padding-bottom: 12px;
  cursor: pointer;

  &.active {
    .step-title {
      color: var(--color-foreground);
      font-weight: 600;
    }

    .step-divider {
      background-color: var(--color-border);
    }
  }
}

.step-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  transition: all 0.2s;

  &:hover {
    .step-title:not(.active) {
      color: var(--color-foreground);
    }
  }
}

.step-title {
  font-size: 16px;
  transition: all 0.2s;
  color: var(--color-muted-foreground);
  font-weight: normal;
}

.step-divider {
  height: 1px;
  width: 100%;
  background-color: var(--color-border);
  transition: background-color 0.2s;
}

// .action-button {
//   width: 100%;
//   padding: 12px 24px;
//   background-color: var(--target-primary);
//   color: white;
//   border: none;
//   border-radius: var(--radius-md);
//   font-weight: 500;
//   cursor: pointer;
//   transition: all 0.2s;

//   &:hover {
//     opacity: 0.9;
//     transform: translateY(-1px);
//   }

//   &:active {
//     transform: translateY(0);
//   }
// }
</style>
